import React from "react";
import { useTranslation } from "react-i18next";
import { Type } from "lucide-react";
import { applyTextSize, TEXT_SIZE_OPTIONS } from "@/lib/textSize";
import { useAuth } from "@/contexts/AuthContext";
import { SettingsCard, useSaveProfile } from "./primitives";

/**
 * TextSizeSection — 4-way Aa picker (sm/md/lg/xl). `applyTextSize`
 * flips a `<html>` class so the change is instant across the app; we
 * then persist so it sticks across sessions.
 */
export default function TextSizeSection() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { saveProfile } = useSaveProfile();

  const onChangeTextSize = (value) => {
    if (!value || value === user?.text_size) return;
    applyTextSize(value);
    saveProfile({ text_size: value }, t("settings.textSizeSaved"));
  };

  return (
    <SettingsCard
      icon={<Type className="w-4 h-4" strokeWidth={1.5} />}
      title={t("settings.textSizeTitle")}
      help={t("settings.textSizeHelp")}
    >
      <div role="radiogroup" aria-label={t("settings.textSizeTitle")} className="grid grid-cols-4 gap-2">
        {TEXT_SIZE_OPTIONS.map((sz) => {
          const fontPx = { sm: 12, md: 14, lg: 16, xl: 18 }[sz];
          const active = (user?.text_size || "md") === sz;
          return (
            <button
              key={sz}
              type="button"
              role="radio"
              aria-checked={active}
              data-testid={`text-size-${sz}`}
              onClick={() => onChangeTextSize(sz)}
              className={`rounded-2xl py-3 border transition flex flex-col items-center gap-1 ${
                active
                  ? "bg-leaf text-white border-leaf"
                  : "bg-white text-leaf-ink border-[#E8E5DF] hover:bg-parchment"
              }`}
            >
              <span className="font-display" style={{ fontSize: `${fontPx}px`, lineHeight: 1 }}>Aa</span>
              <span className="text-[10px] uppercase tracking-[0.14em] font-medium">
                {t(`settings.textSizes.${sz}`)}
              </span>
            </button>
          );
        })}
      </div>
    </SettingsCard>
  );
}
